import React, { useState } from "react";
import { Button, TextField, CircularProgress } from "@mui/material";
import { completeForgotPassword, forgotPassword } from "../cognito";
import { VerificationCodeInput } from "./verificationCode.tsx";
import PasswordInput from "./passwordInput.tsx";
import { ACTION, CATEGORY, evt } from "../common/analytics.ts";
import ErrorMessage from "./ErrorMessage.tsx";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface NewPasswordFormProps {
  onNewPasswordComplete: (email?: string) => void;
  onError?: (errMsg: string) => void;
  showErrors?: boolean;
  trackEvents?: boolean;
}

//endregion [[ Props ]]
export const NewPasswordForm = ({
  onNewPasswordComplete,
  onError,
  showErrors = true,
  trackEvents = true,
}: NewPasswordFormProps) => {
  const [password, setPassword] = useState("");
  const [newPasswordRequested, setNewPasswordRequested] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [invalidPassword, setInvalidPassword] = useState(false);

  const disableSend =
    loading ||
    (newPasswordRequested
      ? !!invalidPassword || !verificationCode || !password
      : !email);

  const handleError = (error: any) => {
    setError(error);
    onError && onError(error);
  };

  const resetError = () => {
    handleError("");
  };

  const onForgetPassword = async () => {
    setLoading(true);
    resetError();
    try {
      await forgotPassword(email);
      setNewPasswordRequested(true);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleError(e as string);
    }
  };

  const onChange = async () => {
    setLoading(true);
    resetError();
    try {
      await completeForgotPassword(email, verificationCode, password);
      setLoading(false);
      onNewPasswordComplete(email);
      if (trackEvents) {
        evt({
          category: CATEGORY.USER_AUTHENTICATION,
          action: ACTION.RESET,
          props: {
            userId: email,
          },
        });
      }
    } catch (e) {
      setLoading(false);
      handleError(e as string);
    }
  };

  const handleEmailChanged = (e) => setEmail(e.target.value);

  return (
    <>
      {!newPasswordRequested && (
        <TextField
          className={"email-input"}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          label="Email Address"
          type="email"
          onKeyDown={(e) => {
            if (e.key === "Enter") onForgetPassword();
          }}
          onChange={handleEmailChanged}
        />
      )}
      {newPasswordRequested && (
        <>
          <PasswordInput
            onChange={(newVal) => setPassword(newVal)}
            onInvalid={(invalid) => setInvalidPassword(invalid)}
          />
          <VerificationCodeInput
            email={email}
            onError={handleError}
            verifying={false}
            onChange={(code) => setVerificationCode(code)}
          />
        </>
      )}
      <Button
        variant="contained"
        fullWidth
        color={"primary"}
        onClick={() => (newPasswordRequested ? onChange() : onForgetPassword())}
        disabled={disableSend}
        className="submit-button"
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : newPasswordRequested ? (
          "Change Password"
        ) : (
          "Forgot Password"
        )}
      </Button>
      {error && showErrors && <ErrorMessage error={error} />}
    </>
  );
};
